/**
 * BlogHeader — Visual article header with mascot, category gradient, and metadata.
 *
 * Each category gets its own color treatment + mascot character.
 */

import { motion } from 'framer-motion';
import type { BlogPost } from '../../types/blog';
import { CATEGORY_CONFIG, CATEGORY_MASCOT } from '../../types/blog';
import { useLangStore } from '../../store/langStore';
import { Mascot } from '../mascots/Mascot';

interface BlogHeaderProps {
  post: BlogPost;
}

export function BlogHeader({ post }: BlogHeaderProps) {
  const { lang } = useLangStore();
  const cat = CATEGORY_CONFIG[post.category];
  const mascotSubject = CATEGORY_MASCOT[post.category];
  const categoryLabel = lang === 'no' ? cat.labelNo : cat.labelEn;

  const formattedDate = new Date(post.date).toLocaleDateString(
    lang === 'no' ? 'nb-NO' : 'en-US',
    { year: 'numeric', month: 'long', day: 'numeric' },
  );

  return (
    <motion.header
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      className="max-w-3xl mx-auto text-center mb-12 relative"
    >
      {/* Decorative color blob behind mascot */}
      <div className="flex justify-center mb-6 relative">
        <div
          className="absolute w-28 h-28 rounded-full blur-2xl opacity-20"
          style={{ backgroundColor: cat.color }}
        />
        <motion.div
          initial={{ scale: 0, rotate: -20 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: 'spring', stiffness: 200, damping: 15, delay: 0.2 }}
        >
          <Mascot subject={mascotSubject} emotion="happy" size={80} />
        </motion.div>
      </div>

      {/* Category badge */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.15 }}
        className="flex justify-center mb-5"
      >
        <span
          className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-display font-semibold text-white shadow-sm"
          style={{ backgroundColor: cat.color }}
        >
          {cat.icon} {categoryLabel}
        </span>
      </motion.div>

      {/* Title */}
      <h1 className="font-display font-bold text-ink text-3xl md:text-4xl lg:text-5xl leading-tight mb-6">
        {post.title}
      </h1>

      {/* Description */}
      <p className="font-body text-ink-muted text-lg md:text-xl leading-relaxed mb-6 max-w-2xl mx-auto">
        {post.description}
      </p>

      {/* Author + meta */}
      <div className="flex items-center justify-center gap-4 text-sm text-ink-faint font-display">
        <span className="font-medium text-ink">{post.author}</span>
        <span aria-hidden="true">·</span>
        <time dateTime={post.date}>{formattedDate}</time>
        <span aria-hidden="true">·</span>
        <span>{post.readingTime} min {lang === 'no' ? 'lesetid' : 'read'}</span>
      </div>

      {/* Tags */}
      {post.tags.length > 0 && (
        <div className="flex flex-wrap justify-center gap-2 mt-5">
          {post.tags.map(tag => (
            <span
              key={tag}
              className="px-2.5 py-0.5 rounded-full text-xs font-display text-ink-muted border border-gray-200"
              style={{ backgroundColor: `${cat.color}08` }}
            >
              #{tag}
            </span>
          ))}
        </div>
      )}

      {/* Bottom gradient divider */}
      <div className="mt-10 h-px w-full relative">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            background: `linear-gradient(to right, transparent, ${cat.color}, transparent)`,
          }}
        />
      </div>
    </motion.header>
  );
}
